/**
 * ServiceAdvisorCard Component
 *
 * Main container for the Service Advisor Performance dashboard.
 * Includes the sortable advisor list and pie chart visualization.
 */

import React from 'react';
import {
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../LoadingSpinner';
import {
  ServiceAdvisor,
  ServiceAdvisorSummary,
  SortConfig,
} from './types';
import { ServiceAdvisorList } from './ServiceAdvisorList';
import { ServiceAdvisorPieChart } from './ServiceAdvisorPieChart';

interface ServiceAdvisorCardProps {
  advisors: ServiceAdvisor[];
  summary: ServiceAdvisorSummary | null;
  loading: boolean;
  refreshing?: boolean;
  error: string | null;
  year: number;
  month: number;
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
  onRefresh?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showHeaderTotals?: boolean;
}

/**
 * Get month name from number
 */
function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

/**
 * Format currency for display
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export const ServiceAdvisorCard: React.FC<ServiceAdvisorCardProps> = ({
  advisors,
  summary,
  loading,
  refreshing = false,
  error,
  year,
  month,
  sortConfig,
  onSortChange,
  onRefresh,
  collapsed = false,
  onToggleCollapse,
  showHeaderTotals = true,
}) => {
  // Calculate total labor gross for pie chart
  const totalLaborGross = summary?.totalLaborGross ??
    advisors.reduce((sum, a) => sum + a.laborGrossTotal, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Service Advisor Performance
              </h3>
              <p className="text-sm text-gray-500">
                {getMonthName(month)} {year} - Labor Gross by Advisor
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!collapsed && onRefresh && (
              <button
                onClick={(e) => { e.stopPropagation(); onRefresh(); }}
                disabled={loading || refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Refresh service advisor data from DealerBuilt"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            {!collapsed && showHeaderTotals && summary && (
              <div className="text-right">
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(totalLaborGross + (summary.subletGross ?? 0))}
                </p>
                <p className="text-sm text-gray-500">
                  Total Department Gross
                </p>
                <div className="flex items-center justify-end gap-3 mt-1 text-sm text-gray-500">
                  <span>{formatCurrency(totalLaborGross)} Labor</span>
                  <span className="text-gray-300">|</span>
                  <span>{formatCurrency(summary.subletGross ?? 0)} Sublet</span>
                </div>
              </div>
            )}
            {onToggleCollapse && (
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
              />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!collapsed && <div className="p-4">
        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Loading advisor data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium">
                  Error Loading Data
                </h4>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Data Display */}
        {!loading && !error && (
          <>
            {advisors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No service advisor data available for this period.</p>
                <p className="text-sm mt-1">
                  Data is loaded nightly from DealerBuilt.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Advisor List - 2/3 width on large screens */}
                <div className="lg:col-span-2">
                  <ServiceAdvisorList
                    advisors={advisors}
                    sortConfig={sortConfig}
                    onSortChange={onSortChange}
                  />
                </div>

                {/* Pie Chart - 1/3 width on large screens */}
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">
                      Gross Distribution
                    </h4>
                    <ServiceAdvisorPieChart
                      advisors={advisors}
                      totalLaborGross={totalLaborGross}
                    />
                  </div>

                  {/*
                    Location-wide CP+SC avg hours/RO — GL-sourced (same data the
                    TSM Ranking Report By-Location row uses). Updated by the
                    nightly service-advisor-loader; locks in at day-5 post-rollover.
                  */}
                  {summary?.avgHoursPerRoCpSc !== undefined && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-1">
                        Location Avg Hrs / RO (CP+SC)
                      </div>
                      <div className="text-3xl font-bold text-blue-900 tabular-nums">
                        {summary.avgHoursPerRoCpSc.toFixed(2)}
                      </div>
                      {summary.cpScRoCount !== undefined && summary.cpScTotalHours !== undefined && summary.cpScRoCount > 0 && (
                        <div className="text-xs text-blue-700 mt-1 tabular-nums">
                          {summary.cpScTotalHours.toFixed(1)} hrs ÷ {summary.cpScRoCount.toLocaleString()} ROs
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>}

      {/* Footer - Last Updated */}
      {!collapsed && summary?.lastUpdated && !loading && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500 text-center">
            Last updated:{' '}
            {new Date(summary.lastUpdated).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
